import React, { useState, useEffect } from 'react';
import useDialog from '../hooks/useDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from '../contexts/LanguageContext';
import { getTutorialSteps } from './tutorialContent';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import './TutorialModal.css';

/**
 * TutorialModal - Onboarding tutorial that explains how to play Word Labyrinth.
 *
 * Shows automatically on first visit (stored in localStorage).
 * Can be accessed anytime via the help button in the navigation bar.
 */
function TutorialModal({ show, onClose }) {
    const { t, language } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const steps = getTutorialSteps(language, t);

    useBodyScrollLock(show);

    const dialogRef = useDialog(!!show, onClose);

    // Reset to first step when modal opens
    useEffect(() => {
        if (show) {
            setCurrentStep(0);
        }
    }, [show]);

    if (!show) {
        return null;
    }

    const totalSteps = steps.length;
    const step = steps[currentStep];
    const isLastStep = currentStep === totalSteps - 1;
    const isFirstStep = currentStep === 0;

    const handleNext = () => {
        if (isLastStep) {
            onClose();
        } else {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (!isFirstStep) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleSkip = () => {
        onClose();
    };

    return (
        <div className="modal-overlay-base tutorial-overlay" onClick={handleOverlayClick}>
            <div className="modal-content-base tutorial-modal" data-modal-scrollable ref={dialogRef} role="dialog" aria-modal="true">
                <button
                    className="tutorial-close"
                    onClick={onClose}
                    aria-label={t('common.close')}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>

                {/* Progress dots */}
                <div className="tutorial-progress">
                    {steps.map((_, index) => (
                        <button
                            key={index}
                            className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                            onClick={() => setCurrentStep(index)}
                            aria-label={t('tutorial.goToStep', { n: index + 1 })}
                        />
                    ))}
                </div>

                {/* Step content */}
                <div className="tutorial-content">
                    <h2 className="tutorial-title">{step.title}</h2>
                    <p className="tutorial-description">{step.description}</p>

                    {/* Visual example */}
                    {step.visual && (
                        <div className="tutorial-visual">
                            {step.visual}
                        </div>
                    )}
                </div>

                {/* Navigation buttons */}
                <div className="tutorial-actions">
                    <button className="tutorial-button secondary" onClick={isFirstStep ? handleSkip : handlePrevious}>
                        <span>{isFirstStep ? t('common.skip') : t('common.back')}</span>
                    </button>

                    <button className="tutorial-button primary" onClick={handleNext}>
                        {isLastStep ? (
                            <>
                                <FontAwesomeIcon icon={faCheck} />
                                <span>{t('common.gotIt')}</span>
                            </>
                        ) : (
                            <>
                                <span>{t('common.next')}</span>
                                <FontAwesomeIcon icon={faArrowRight} />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default TutorialModal;
